'use client'

/**
 * SiteGuideView — the reading surface for /site-guide.
 *
 * Owner 2026-08-07: "one long scrollable page ... document every page ... best in class."
 *
 * ── WHY IT IS BUILT THIS WAY ─────────────────────────────────────────────────────────────────────
 * · ONE PAGE, NOT SUB-PAGES. Everything stays in the DOM so browser find-in-page works across the
 *   whole site index, and so a single URL carries all of it for search engines.
 * · SEARCH FILTERS, IT DOES NOT NAVIGATE. Typing narrows in place and reports how many matched, so
 *   "is there a page for X" is answerable in one keystroke without losing your place.
 * · THE CONTENT IS SERVER-RENDERED BY THE PARENT and passed in. This component only adds the TOC,
 *   the active-section highlight and the filter. If search were the only way to reach an entry, the
 *   entries would be invisible to crawlers.
 * · EVERY ENTRY IS A REAL LINK. The index is only useful if you can click through to the thing.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import Link from 'next/link'
import {
  Search, X, ArrowUpRight, Link2 as LinkIcon, Rocket, Bell, Radar, TrendingUp, ListFilter,
  CandlestickChart, Bot, Landmark, CalendarDays, Users, Calculator, BookOpen, Settings,
} from 'lucide-react'
import type { GuideSection } from '@/lib/siteGuide'

/**
 * Per-section icon and accent. Keyed by section id, and kept HERE rather than in siteGuide.ts so the
 * content file stays a plain data module with no React or lucide import: it is read by a server
 * component, and pulling an icon library into it would drag the client bundle along for no reason.
 * ⚠ A missing id falls back to a neutral chip rather than crashing, so adding a section to the
 * content file can never break the page before this map is updated.
 */
const SECTION_LOOK: Record<string, { Icon: typeof Rocket; ring: string; text: string; glow: string; bar: string }> = {
  start:       { Icon: Rocket,           ring: 'ring-amber-500/30',   text: 'text-amber-400',   glow: 'from-amber-500/10', bar: 'hover:before:bg-amber-500/70' },
  alerts:      { Icon: Bell,             ring: 'ring-rose-500/30',    text: 'text-rose-400',    glow: 'from-rose-500/10', bar: 'hover:before:bg-rose-500/70' },
  scanners:    { Icon: Radar,            ring: 'ring-sky-500/30',     text: 'text-sky-400',     glow: 'from-sky-500/10', bar: 'hover:before:bg-sky-500/70' },
  movers:      { Icon: TrendingUp,       ring: 'ring-emerald-500/30', text: 'text-emerald-400', glow: 'from-emerald-500/10', bar: 'hover:before:bg-emerald-500/70' },
  screens:     { Icon: ListFilter,       ring: 'ring-violet-500/30',  text: 'text-violet-400',  glow: 'from-violet-500/10', bar: 'hover:before:bg-violet-500/70' },
  charts:      { Icon: CandlestickChart, ring: 'ring-cyan-500/30',    text: 'text-cyan-400',    glow: 'from-cyan-500/10', bar: 'hover:before:bg-cyan-500/70' },
  watcher:     { Icon: Bot,              ring: 'ring-fuchsia-500/30', text: 'text-fuchsia-400', glow: 'from-fuchsia-500/10', bar: 'hover:before:bg-fuchsia-500/70' },
  money:       { Icon: Landmark,         ring: 'ring-lime-500/30',    text: 'text-lime-400',    glow: 'from-lime-500/10', bar: 'hover:before:bg-lime-500/70' },
  calendars:   { Icon: CalendarDays,     ring: 'ring-orange-500/30',  text: 'text-orange-400',  glow: 'from-orange-500/10', bar: 'hover:before:bg-orange-500/70' },
  community:   { Icon: Users,            ring: 'ring-pink-500/30',    text: 'text-pink-400',    glow: 'from-pink-500/10', bar: 'hover:before:bg-pink-500/70' },
  calculators: { Icon: Calculator,       ring: 'ring-teal-500/30',    text: 'text-teal-400',    glow: 'from-teal-500/10', bar: 'hover:before:bg-teal-500/70' },
  learn:       { Icon: BookOpen,         ring: 'ring-indigo-500/30',  text: 'text-indigo-400',  glow: 'from-indigo-500/10', bar: 'hover:before:bg-indigo-500/70' },
  account:     { Icon: Settings,         ring: 'ring-zinc-500/30',    text: 'text-zinc-300',    glow: 'from-zinc-500/10', bar: 'hover:before:bg-zinc-500/70' },
}
const FALLBACK_LOOK = { Icon: BookOpen, ring: 'ring-zinc-700', text: 'text-zinc-300', glow: 'from-zinc-500/10', bar: 'hover:before:bg-zinc-500/70' }

const TAG_STYLE: Record<string, string> = {
  free: 'bg-emerald-500/10 text-emerald-300 ring-emerald-500/25',
  premium: 'bg-amber-500/10 text-amber-300 ring-amber-500/25',
  account: 'bg-sky-500/10 text-sky-300 ring-sky-500/25',
  beta: 'bg-violet-500/10 text-violet-300 ring-violet-500/25',
}
const TAG_LABEL: Record<string, string> = {
  free: 'free',
  premium: 'premium',
  account: 'sign-in',
  beta: 'beta',
}

export default function SiteGuideView({ sections, total }: { sections: GuideSection[]; total: number }) {
  const [q, setQ] = useState('')
  const [active, setActive] = useState<string>(sections[0]?.id ?? '')
  const inputRef = useRef<HTMLInputElement>(null)

  const query = q.trim().toLowerCase()

  const filtered = useMemo(() => {
    if (!query) return sections
    return sections
      .map((s) => ({
        ...s,
        entries: s.entries.filter((e) =>
          `${e.name} ${e.href} ${e.what} ${e.how}`.toLowerCase().includes(query),
        ),
      }))
      .filter((s) => s.entries.length > 0)
  }, [sections, query])

  const matchCount = useMemo(
    () => filtered.reduce((n, s) => n + s.entries.length, 0),
    [filtered],
  )

  // Highlight the section currently in view. Disabled while filtering, because sections appear and
  // disappear and the highlight would chase them around.
  useEffect(() => {
    if (query) return
    const els = sections
      .map((s) => document.getElementById(s.id))
      .filter((el): el is HTMLElement => !!el)
    if (!els.length) return
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0]
        if (visible?.target?.id) setActive(visible.target.id)
      },
      // Top-weighted band so a heading counts as "active" once it reaches the upper third.
      { rootMargin: '-80px 0px -70% 0px', threshold: 0 },
    )
    els.forEach((el) => obs.observe(el))
    return () => obs.disconnect()
  }, [sections, query])

  // "/" focuses search, the convention on documentation sites.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null
      const typing = t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)
      if (e.key === '/' && !typing) {
        e.preventDefault()
        inputRef.current?.focus()
      }
      if (e.key === 'Escape') setQ('')
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 pb-24 sm:px-6">
      <div className="lg:grid lg:grid-cols-[240px_minmax(0,1fr)] lg:gap-10">
        {/* ── TABLE OF CONTENTS ─────────────────────────────────────────────────── */}
        <aside className="hidden lg:block">
          <nav className="sticky top-8 max-h-[calc(100vh-4rem)] overflow-y-auto pb-8" aria-label="Sections">
            <p className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
              {total} pages documented
            </p>
            <ul className="space-y-0.5">
              {sections.map((s) => {
                const on = !query && s.id === active
                const look = SECTION_LOOK[s.id] ?? FALLBACK_LOOK
                return (
                  <li key={s.id}>
                    <a
                      href={`#${s.id}`}
                      className={`flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm transition ${
                        on
                          ? 'bg-zinc-800 font-semibold text-white'
                          : 'text-zinc-400 hover:bg-zinc-900 hover:text-zinc-200'
                      }`}
                    >
                      <look.Icon className={`h-3.5 w-3.5 shrink-0 ${on ? look.text : 'text-zinc-600'}`} />
                      <span className="truncate">{s.title}</span>
                      <span className="ml-auto text-[11px] text-zinc-600">{s.entries.length}</span>
                    </a>
                  </li>
                )
              })}
            </ul>
          </nav>
        </aside>

        <div className="min-w-0">
          {/* ── SEARCH ──────────────────────────────────────────────────────────── */}
          <div className="sticky top-0 z-20 -mx-4 mb-8 bg-zinc-950/90 px-4 py-4 backdrop-blur sm:-mx-6 sm:px-6">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
              <input
                ref={inputRef}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                type="search"
                placeholder="Search every page.  Try halt, RVOL, position size, dividends..."
                aria-label="Search the site guide"
                className="w-full rounded-xl border border-zinc-800 bg-zinc-900/70 py-3 pl-10 pr-24 text-sm text-white
                           placeholder:text-zinc-500 focus:border-sky-500/50 focus:outline-none focus:ring-2 focus:ring-sky-500/25"
              />
              {q ? (
                <button
                  type="button"
                  onClick={() => setQ('')}
                  aria-label="Clear search"
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-1 text-zinc-400 hover:bg-zinc-800 hover:text-white"
                >
                  <X className="h-4 w-4" />
                </button>
              ) : (
                <kbd className="pointer-events-none absolute right-3 top-1/2 hidden -translate-y-1/2 rounded border border-zinc-800 bg-zinc-900 px-1.5 py-0.5 text-[10px] font-medium text-zinc-500 sm:block">
                  press /
                </kbd>
              )}
            </div>
            {query ? (
              <p className="mt-2 text-xs text-zinc-500">
                {matchCount === 0
                  ? 'No pages match. Try a shorter word.'
                  : `${matchCount} page${matchCount === 1 ? '' : 's'} match "${q.trim()}"`}
              </p>
            ) : null}

            {/* ── MOBILE SECTION JUMP ───────────────────────────────────────────────────────────
                The sidebar TOC is lg-only, so on a phone this page was 124 entries with no way to
                navigate but scrolling. These chips are that navigation. Hidden while filtering,
                because the sections on screen are then the search result, not the outline. */}
            {!query ? (
              <div className="-mx-4 mt-3 overflow-x-auto px-4 lg:hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                <div className="flex w-max gap-1.5 pb-1">
                  {sections.map((s) => {
                    const look = SECTION_LOOK[s.id] ?? FALLBACK_LOOK
                    return (
                      <a
                        key={s.id}
                        href={`#${s.id}`}
                        className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-zinc-800 bg-zinc-900 px-2.5 py-1.5 text-xs font-medium text-zinc-300 active:bg-zinc-800"
                      >
                        <look.Icon className={`h-3.5 w-3.5 ${look.text}`} />
                        {s.title}
                        <span className="text-[10px] text-zinc-600">{s.entries.length}</span>
                      </a>
                    )
                  })}
                </div>
              </div>
            ) : null}
          </div>

          {/* ── SECTIONS ────────────────────────────────────────────────────────── */}
          {filtered.map((s) => {
            const look = SECTION_LOOK[s.id] ?? FALLBACK_LOOK
            return (
            <section key={s.id} id={s.id} className="mb-14 scroll-mt-24">
              <div className={`relative mb-5 overflow-hidden rounded-xl border border-zinc-800 bg-gradient-to-r ${look.glow} to-transparent p-4`}>
                <div className="flex items-center gap-3">
                  <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-zinc-950/60 ring-1 ${look.ring}`}>
                    <look.Icon className={`h-5 w-5 ${look.text}`} />
                  </span>
                  <div className="min-w-0">
                    <h2 className="flex items-baseline gap-2 text-xl font-black tracking-tight text-white sm:text-2xl">
                      <a href={`#${s.id}`} className="hover:text-sky-300">{s.title}</a>
                      <span className="text-xs font-medium text-zinc-600">{s.entries.length} pages</span>
                    </h2>
                    <p className="mt-1 max-w-3xl text-sm leading-relaxed text-zinc-400">{s.blurb}</p>
                  </div>
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                {s.entries.map((e) => {
                  // A route pattern like /chart/[symbol] is documentation, not a working link:
                  // it needs a real symbol. Rendered as plain text so the index never ships a 404.
                  const isPattern = e.href.includes('[')
                  // A stable per-entry anchor, derived from the route it documents. The guide's
                  // whole purpose is being shared, and until now the smallest shareable unit was
                  // the entire 124-entry page.
                  // Readable, because these get pasted to people: /rvol-scanner -> page-rvol-scanner,
                  // and the bare homepage route -> page-home rather than a lone 'p'.
                  const slug = e.href.replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-|-$/g, '')
                  const anchor = `page-${slug || 'home'}`
                  return (
                    <article
                      id={anchor}
                      key={e.href + e.name}
                      className={`group relative scroll-mt-28 overflow-hidden rounded-xl border border-zinc-800 bg-zinc-900/40 p-4 pl-5 transition
                                  hover:border-zinc-600 hover:bg-zinc-900/80
                                  before:absolute before:inset-y-0 before:left-0 before:w-[3px] before:bg-zinc-800
                                  before:transition-colors before:content-[''] ${look.bar}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <h3 className="flex items-center gap-1.5 font-bold text-white">
                            {isPattern ? (
                              <span>{e.name}</span>
                            ) : (
                              <Link href={e.href} className="hover:text-sky-300">
                                {e.name}
                              </Link>
                            )}
                            {!isPattern ? (
                              <ArrowUpRight className="h-3.5 w-3.5 shrink-0 text-zinc-600 transition group-hover:text-sky-400" />
                            ) : null}
                            <a
                              href={`#${anchor}`}
                              aria-label={`Link to ${e.name}`}
                              title="Link to this entry"
                              className="ml-auto shrink-0 text-zinc-700 opacity-0 transition hover:text-sky-400 focus:opacity-100 group-hover:opacity-100"
                            >
                              <LinkIcon className="h-3 w-3" />
                            </a>
                          </h3>
                          <code className="mt-0.5 block truncate text-[11px] text-zinc-500">{e.href}</code>
                        </div>
                        {e.tag ? (
                          <span
                            className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1 ${TAG_STYLE[e.tag]}`}
                          >
                            {TAG_LABEL[e.tag]}
                          </span>
                        ) : null}
                      </div>

                      <dl className="mt-3 space-y-2 text-sm leading-relaxed">
                        <div>
                          <dt className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
                            What you see
                          </dt>
                          <dd className="text-zinc-300">{e.what}</dd>
                        </div>
                        <div>
                          <dt className="text-[10px] font-semibold uppercase tracking-wider text-sky-500/80">
                            How to use it
                          </dt>
                          <dd className="text-zinc-400">{e.how}</dd>
                        </div>
                        {/* The manual layer: the actual things on screen and how to read each one.
                            Optional on purpose. An entry without it is one nobody has verified yet,
                            which is a better state than confidently describing columns that do not
                            exist. See the warning on GuideEntry.detail. */}
                        {e.detail?.length ? (
                          <div>
                            <dt className="text-[10px] font-semibold uppercase tracking-wider text-amber-500/80">
                              Reading the numbers
                            </dt>
                            <dd>
                              <ul className="mt-1 space-y-1">
                                {e.detail.map((d, di) => (
                                  <li key={di} className="flex gap-2 text-[13px] leading-relaxed text-zinc-400">
                                    <span className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-amber-500/60" />
                                    <span>{d}</span>
                                  </li>
                                ))}
                              </ul>
                            </dd>
                          </div>
                        ) : null}
                      </dl>
                    </article>
                  )
                })}
              </div>
            </section>
            )
          })}

          {query && matchCount === 0 ? (
            <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-10 text-center">
              <p className="font-semibold text-white">Nothing matched &ldquo;{q.trim()}&rdquo;</p>
              <button
                type="button"
                onClick={() => setQ('')}
                className="mt-3 rounded-lg bg-zinc-800 px-3 py-1.5 text-sm font-semibold text-white hover:bg-white/20"
              >
                Clear search
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}
